import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { Journey } from 'src/common/interfaces/journey.interface';
import { useCurrentOrganization } from 'src/store/organization';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';
import { DesktopStrongIcon, MobileStrongIcon, TabletStrongIcon, UnknownStrongIcon } from 'src/monet/icons';
import { formatDuration } from 'src/utils/date';
import { DataTable } from 'src/common/components/table/data-table';
import { TooltipComponent } from 'src/common/tooltip/component';
import { useHistory } from 'react-router-dom';
import { useJourneyInsights } from 'src/dashboard/insights/journeys/use-journey-insights.hook';
import { VisitorView } from 'src/editor/insights/constants';
import { getDeviceTypeTooltip } from 'src/editor/insights/visitor-card';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { Row } from 'react-table';
import { DashboardInsightsJourneysJourneyUtmDetailsSlideover } from './utm-details-slideover';
import { getJourneyAliasById } from '../../helpers/get-journey-alias-by-id';
import { EllipsizedText } from 'src/common/components/table/ellipsized-text';
import { TimePeriodParam } from '../../constants';
import { TableHeaderWithTooltip } from '../../table-header-with-tooltip';
import { formatPhoneNumber } from 'src/player/JourneyEmailGating';

type Props = {
  journey: Journey;
  timePeriodParam?: TimePeriodParam;
  setVisitorViews: any;
  setAllVisitorsFetcher: (fetcher: () => Promise<{ views: VisitorView[] }>) => void;
};

const SLIDE_OVER_ANIMATION_DURATION_IN_MS = 200;

export const DashboardInsightsJourneysJourneyVisitorsTable = ({
  journey,
  timePeriodParam,
  setAllVisitorsFetcher,
  setVisitorViews,
}: Props) => {
  const {
    filterBarParams: { search_term, page, filters },
    updatePage,
  } = useFilterBarParams();

  const history = useHistory();
  const { currentOrganization } = useCurrentOrganization();
  const [utmPanelOpen, setUtmPanelOpen] = useState(false);
  const [utmParams, setUtmParams] = useState<string>();

  // We use useMemo here so that we pass the same object to the api hook below.
  const insightsFilters = useMemo(
    () => ({
      email: search_term,
      node_ids: filters?.step?.map((nodeId) => parseInt(nodeId)),
      alias_ids: filters?.link?.map((aliasId) => parseInt(aliasId)),
      time_period: timePeriodParam,
      all: undefined,
    }),
    [search_term, filters, timePeriodParam]
  );

  const allInsightsFilters = useMemo(
    () => ({
      email: search_term,
      node_ids: filters?.step?.map((nodeId) => parseInt(nodeId)),
      alias_ids: filters?.link?.map((aliasId) => parseInt(aliasId)),
      time_period: timePeriodParam,
      all: true,
    }),
    [search_term, filters, timePeriodParam]
  );

  const { fetching, pages, showPagination, visitorViews } = useJourneyInsights({
    journeyUuid: journey.uuid,
    page,
    filters: insightsFilters,
  });

  const { fetchJourneyInsights } = useJourneyInsights({
    journeyUuid: journey.uuid,
    page,
    filters: allInsightsFilters,
    preventImmediateFetching: true,
  });

  useEffect(() => {
    setAllVisitorsFetcher(fetchJourneyInsights);
  }, [fetchJourneyInsights]);

  const columns = useMemo(
    () => [
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor name or email'>Name</TableHeaderWithTooltip>,
        accessor: 'name',
        width: 176,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <EllipsizedText text={row.original.name || 'Anonymous'} />
        ),
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='When they viewed'>Viewed</TableHeaderWithTooltip>,
        accessor: 'viewed',
        width: 100,
        Cell: ({ row, index }: { row: any; index: number }) => row.original.created_at,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Time spent viewing Journey'>Time spent</TableHeaderWithTooltip>,
        accessor: 'time',
        width: 80,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { duration } = visitorView;
          return duration ? formatDuration(duration) : '-';
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='How many steps were viewed'>Completion</TableHeaderWithTooltip>,
        accessor: 'completion',
        width: 100,
        Cell: ({ row, index }: { row: any; index: number }) => `${Math.round(row.original.completed_percentage)}%`,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor email if captured'>Email address</TableHeaderWithTooltip>,
        accessor: 'email_address',
        width: 196,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => <EllipsizedText text={row.original.email || ''} />,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Website domain'>Website</TableHeaderWithTooltip>,
        accessor: 'website',
        allowShrinking: true,
        width: '100%',
        Cell: ({ row }: { row: any }) => <EllipsizedText text={row.original.website || ''} />,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Phone number'>Phone Number</TableHeaderWithTooltip>,
        accessor: 'phone_number',
        width: 100,
        Cell: ({ row }: { row: any }) => {
          let customFieldValues: VisitorView['visitor']['custom_field_values'] =
            row.original.visitor.custom_field_values;
          if (!customFieldValues) {
            return '';
          }
          if (!Array.isArray(customFieldValues) && typeof customFieldValues === 'object') {
            customFieldValues = Object.values(customFieldValues);
            customFieldValues = customFieldValues.filter((a) => typeof a !== 'string');
          }
          const phoneNumberField = customFieldValues.find((field) => field.field === 'phone_number');
          return formatPhoneNumber(phoneNumberField?.value || '');
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Inferred company name'>Company</TableHeaderWithTooltip>,
        accessor: 'company',
        width: 138,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { company } = visitorView;
          if (company) {
            return (
              <div className='flex items-start gap-2'>
                {company.logo && <ImageRenderer className='rounded-md w-5 h-5' src={company.logo} />}
                {company.name && <div>{company.name}</div>}
              </div>
            );
          }
          return null;
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Location of viewer'>Location</TableHeaderWithTooltip>,
        accessor: 'location',
        width: '100%',
        Cell: ({ row, index }: { row: any; index: number }) => row.original.location || '',
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='IP address of viewer'>IP address</TableHeaderWithTooltip>,
        accessor: 'ip_address',
        width: 100,
        Cell: ({ row, index }: { row: any; index: number }) => row.original.ip_address || '',
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Link name that was opened'>Link</TableHeaderWithTooltip>,
        accessor: 'link',
        width: 98,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { alias_id, utm_params } = visitorView;
          if (alias_id) {
            return (
              <div>
                <EllipsizedText text={getJourneyAliasById(journey, alias_id)?.name || ''} />
              </div>
            );
          }
          return null;
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='UTM Parameters'>UTM</TableHeaderWithTooltip>,
        accessor: 'utm',
        width: 120,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { utm_parameters } = visitorView;

          if (utm_parameters && Object.keys(utm_parameters).length > 0) {
            return (
              <div
                className='underline cursor-pointer'
                onClick={(e: any) => {
                  e.stopPropagation();
                  // Format utm_parameters object into string format
                  const utmString = Object.entries(utm_parameters)
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&');
                  setUtmParams(utmString);
                  setUtmPanelOpen(true);
                }}
              >
                View Details
              </div>
            );
          }

          return null;
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Device type'>Device</TableHeaderWithTooltip>,
        accessor: 'device',
        width: '100%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { device_type } = visitorView;
          return (
            <div className='flex'>
              <TooltipComponent
                placement='top'
                tooltipContent={getDeviceTypeTooltip(device_type)}
                styling='tooltip'
                theme='journey'
              >
                {device_type?.form === 'mobile' ? (
                  <MobileStrongIcon />
                ) : device_type?.form === 'desktop' ? (
                  <DesktopStrongIcon />
                ) : device_type?.form === 'tablet' ? (
                  <TabletStrongIcon />
                ) : (
                  <UnknownStrongIcon />
                )}
              </TooltipComponent>
            </div>
          );
        },
      }),
      generateColumn({
        Header: (
          <div style={{ padding: '0 10px' }}>
            <TableHeaderWithTooltip tooltip='Session Replay'>Session Replay</TableHeaderWithTooltip>
          </div>
        ),
        accessor: 'session_replay',
        allowShrinking: true,
        width: 110,
        Cell: ({ row }: { row: any }) => (
          <div style={{ padding: '0 10px' }}>
            <EllipsizedText text={row.original.session_replay_url ? 'Available' : ''} />
          </div>
        ),
      }),
    ],
    [journey, getJourneyAliasById]
  );

  const onPageChange = useCallback(
    (e: any, newPage: number) => {
      updatePage(newPage);
    },
    [updatePage]
  );

  const onRowClicked = (row: Row) => {
    const { session_uuid } = row.original as any;
    history.push(`/${currentOrganization.slug}/insights/journeys/${journey.uuid}/${session_uuid.replace(/-/g, '')}`);
  };

  return (
    <div className='mb-6'>
      {fetching ? (
        <FixedLoadingSpinner />
      ) : visitorViews.length === 0 ? (
        <div className='text-bedrock-p'>No visitors found</div>
      ) : (
        <DataTable tableClassName='w-full' columns={columns} data={visitorViews} onRowClick={onRowClicked} />
      )}
      {showPagination && pages > 1 && (
        <div className='mt-4'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
      <DashboardInsightsJourneysJourneyUtmDetailsSlideover
        open={utmPanelOpen}
        onClose={() => {
          setUtmPanelOpen(false);
          setTimeout(() => {
            setUtmParams(undefined);
          }, SLIDE_OVER_ANIMATION_DURATION_IN_MS);
        }}
        utmParams={utmParams}
      />
    </div>
  );
};
